import { useMemo, useRef, useState } from 'react';
import { localised, useI18n, type CopyKey } from './i18n';
import { isError, setNodeActive, type CoverageView, type PlanNode } from './api';
/* What happened to the subjects that are not here is a section of this same screen now (F-141),
   so this is a jump and not a link. See `goToResearch`. */
import { goToResearch } from './panels';
/*
 * What may be said about coverage, decided in a module with no DOM in it (F-129). The plan
 * screen is where the honest version of a short plan has to live, and "honest" here has a
 * precise meaning: it may say a subject is missing only when something actually knows that it
 * is. See `plan/coverage.ts` — the two live objectives whose nodes name no subject are why.
 */
import { missingFrom, planNotice, titlesBySubject } from './plan/coverage';
/*
 * From the `domain` subpath, not from the package root, and that is not a style
 * choice. This is the first thing the browser imports from `@nabu/shared` for its
 * *value* rather than its type, and the root barrel re-exports the content store,
 * which imports `node:crypto` — Rollup follows it and the production build dies with
 * "createHash is not exported by __vite-browser-external". `domain` has no imports at
 * all, so the shared rule about what counts as active can be read here without
 * dragging a Node module into a phone.
 */
import { isActiveNode, type CurriculumNode } from '@nabu/shared/domain';

/**
 * The plan: read it a subject at a time, and say which subjects you want.
 *
 * Reordering and reweighting are still a later slice (F-50), and this still has no
 * controls for either. What it does have is the two things a 69-node curriculum
 * made unavoidable: every top-level subject collapses, and every top-level subject
 * can be switched off. One flat outline of 69 nodes is a plan nobody can judge —
 * which was the report — and a plan that cannot be narrowed is one where the only
 * way to drop a subject is to throw the whole objective away.
 *
 * Selection is the one thing here that writes. It is still presentational about
 * *lifecycle*: whether a curriculum can exist at all is decided by the status logic
 * in Objectives.tsx, which is why this takes the nodes rather than fetching them.
 * `objectiveId` is here only because the endpoint needs it.
 */

/**
 * How far indentation goes before it stops.
 *
 * Every extra level costs the same characters on every line beneath it, and on a
 * 360px phone a fourth level leaves a column too narrow to read a sentence in.
 * Depth past this point is carried by the outline number alone — `2.3.1.4` says
 * where a topic sits without spending any width on saying it.
 */
const INDENT_LEVELS = 2;

/**
 * Where the walk stops descending, whatever the data says.
 *
 * A `parentId` chain that loops — two nodes claiming each other, a node claiming
 * itself — would recurse until the stack gives out and take the whole screen
 * with it. No real curriculum is fifteen deep, so a ceiling costs nothing and
 * turns a blank page into an odd-looking outline.
 */
const MAX_DEPTH = 15;

/**
 * Weight as a word and a bar, never as the number.
 *
 * `weight` arrives as something like 0.72, and a learner shown 0.72 beside 0.68
 * will try to tell the two apart — a distinction the model that produced them
 * cannot support and that nothing on this screen depends on. The band says what
 * the number is *for*; the bar keeps the ordering the bands flatten, so the
 * heaviest topic within a band still looks like the heaviest.
 *
 * The wording avoids "often" on purpose. Weight is not a scheduling input — how
 * often a topic comes back is decided by what the learner keeps getting wrong —
 * and copy like "asked a lot" would promise something FSRS does not honour.
 */
const BANDS: readonly { atLeast: number; copy: CopyKey }[] = [
  { atLeast: 0.75, copy: 'cur.weight.core' },
  { atLeast: 0.5, copy: 'cur.weight.major' },
  { atLeast: 0.25, copy: 'cur.weight.minor' },
  { atLeast: 0, copy: 'cur.weight.aside' },
];

/*
 * Exported, because the progress panel weights its headline by exactly this number
 * and has to be able to show the learner what it weighted by. A second copy of the
 * thresholds there is how "Core" on one screen becomes "Important" on the next for
 * the same subject.
 */
export function bandOf(weight: number): CopyKey {
  // The last band matches everything at or above zero, so the fallback is only
  // reached by a negative or non-numeric weight — which is a bug upstream, not
  // something to render as a blank badge.
  return BANDS.find((band) => weight >= band.atLeast)?.copy ?? 'cur.weight.aside';
}

/** Clamped, because a weight outside 0–1 would draw a bar outside its track. */
const barWidth = (weight: number) => `${Math.round(Math.min(1, Math.max(0, weight)) * 100)}%`;

/**
 * How many missing subjects are named before the rest fold away.
 *
 * `GAPS_SHOWN` in `Objectives.tsx` is six for the same reason and this is four: a gap is a
 * phrase, where each of these is a subject title *and* a sentence saying what is missing from
 * it. Four of those is already most of a phone screen, and the thing that must stay above the
 * fold is the plan itself — a notice that pushes the plan off the screen is a notice that
 * replaced the thing it is about.
 */
const MISSING_SHOWN = 4;

interface Row {
  node: PlanNode;
  depth: number;
  /** `2.3.1`, computed from position among siblings rather than from `order`. */
  label: string;
}

/** A top-level subject and everything underneath it, in reading order. */
interface Section {
  head: Row;
  children: Row[];
}

/**
 * The tree, flattened into rows that each know their own depth.
 *
 * Flat rather than nested lists because the nesting a learner needs is conveyed
 * by the outline number and a little indentation, and because a flat list is one
 * pass to render and one place to enforce the depth ceiling.
 *
 * The numbering counts siblings as they arrive instead of using `order`, whose
 * values are only guaranteed to sort — a curriculum whose orders are 10, 20, 30
 * would otherwise be numbered 10, 20, 30 on screen.
 *
 * Two things here exist to stop nodes disappearing silently, which is the one
 * failure this panel must not have: a node whose `parentId` names something not
 * in the response is treated as a root rather than dropped, and anything the
 * walk never reached — a loop, or a chain past the ceiling — is appended at the
 * end. A curriculum that looks shorter than it is would have a learner reviewing
 * a plan that is not the plan.
 */
function flatten(nodes: PlanNode[]): Row[] {
  const present = new Set(nodes.map((node) => node.nodeId));
  const children = new Map<string, PlanNode[]>();
  const ROOT = '';

  for (const node of nodes) {
    const parent = node.parentId && present.has(node.parentId) ? node.parentId : ROOT;
    const siblings = children.get(parent);
    if (siblings) siblings.push(node);
    else children.set(parent, [node]);
  }

  const rows: Row[] = [];
  const seen = new Set<string>();

  const walk = (parent: string, depth: number, prefix: string) => {
    if (depth > MAX_DEPTH) return;
    (children.get(parent) ?? []).forEach((node, index) => {
      if (seen.has(node.nodeId)) return;
      seen.add(node.nodeId);
      const label = prefix ? `${prefix}.${index + 1}` : String(index + 1);
      rows.push({ node, depth, label });
      walk(node.nodeId, depth + 1, label);
    });
  };
  walk(ROOT, 0, '');

  for (const node of nodes) {
    if (!seen.has(node.nodeId)) rows.push({ node, depth: 0, label: '·' });
  }

  return rows;
}

/**
 * The same rows, cut into collapsible subjects.
 *
 * A depth-0 row opens a section and everything after it belongs to that section
 * until the next depth-0 row, which works only because `flatten` emits a subtree
 * contiguously. Grouping *its* output rather than walking the tree a second time
 * is deliberate: everything `flatten` does to keep nodes from vanishing — orphans
 * promoted to roots, unreached nodes appended with a `·` — arrives here already
 * done, and a second walk would be a second, quietly different opinion about which
 * nodes exist.
 *
 * The guard for a first row that is not depth 0 is not decoration. `flatten` cannot
 * currently produce one, but if it ever did, appending to a section that does not
 * exist yet would drop the row — and a silently shorter curriculum is exactly the
 * failure the flattening above is written to avoid.
 */
function sectionise(rows: Row[]): Section[] {
  const sections: Section[] = [];
  let current: Section | undefined;

  for (const row of rows) {
    if (row.depth === 0 || !current) {
      current = { head: row, children: [] };
      sections.push(current);
    } else {
      current.children.push(row);
    }
  }

  return sections;
}

/** One nested topic. Nesting deeper than the indentation reaches is carried by the number. */
function TopicRow({ row: { node, depth, label }, subject }: { row: Row; subject?: string }) {
  const { t, locale } = useI18n();
  const summary = localised(node.summary, locale, '');

  return (
    <li className={`node depth-${Math.min(depth, INDENT_LEVELS)}`}>
      <p className="node-head">
        <span className="node-number">{label}</span>
        <span className="node-title">{localised(node.title, locale, t('cur.untitled'))}</span>
      </p>
      {/* Only when this topic came from a different subject than the section it sits in, which
          is what a spliced-in subtree looks like from here. Repeating the section's own subject
          on every one of its eleven children would be eleven copies of a label that says
          nothing new, and it is the section head that carries the general case. */}
      {subject && <p className="node-subject">{t('cov.from').replace('{subject}', subject)}</p>}
      <p className="node-weight">
        <span className="weight-band">{t(bandOf(node.weight))}</span>
        {/* The bar repeats what the band says, less precisely, so it is
            hidden from a screen reader rather than read out as a second,
            wordless opinion. */}
        <span className="weight-bar" aria-hidden="true">
          <span style={{ width: barWidth(node.weight) }} />
        </span>
      </p>
      {summary && <p className="node-summary">{summary}</p>}
    </li>
  );
}

export default function Curriculum({
  objectiveId,
  nodes,
  coverage,
}: {
  objectiveId: string;
  nodes: PlanNode[];
  /**
   * What happened to every researched subject (F-129). Absent while it is being read, and for
   * an objective that has no research behind it at all — a plan built from the learner's own
   * pages has no subjects, and must look like nothing happened rather than like something is
   * missing.
   */
  coverage?: CoverageView;
}) {
  const { t, locale } = useI18n();
  /*
   * Summaries are shortened until asked for, and the choice is one switch for the
   * whole panel rather than one per topic. Twenty topics each carrying three
   * sentences is a plan nobody can see the shape of on a phone, and twenty
   * separate "more" links to fix that is twenty taps and a moving layout.
   */
  const [fullSummaries, setFullSummaries] = useState(false);

  const rows = useMemo(() => flatten(nodes), [nodes]);
  const sections = useMemo(() => sectionise(rows), [rows]);

  /*
   * Which subject each part of the plan came from, and what is not here at all.
   *
   * Empty for a plan built before nodes named their subject, which is deliberate and is the
   * whole compatibility story: no chips, no claims, one sentence saying the two lists cannot be
   * matched up here. The alternative — "unknown subject" on sixty-nine rows — is a screen
   * telling somebody in the middle of a course that their plan is broken when nothing is.
   */
  const subjectTitles = useMemo(() => titlesBySubject(coverage), [coverage]);
  const notice = planNotice(coverage);
  const missing = useMemo(() => missingFrom(coverage), [coverage]);
  const [allMissing, setAllMissing] = useState(false);
  const shownMissing = allMissing ? missing : missing.slice(0, MISSING_SHOWN);

  /*
   * Collapsed to begin with, and that is the whole point of this panel.
   *
   * The report was that the outline is too big to make sense of: 69 topics down one
   * page, where the only way to find the subject you care about is to scroll past
   * every other one. Collapsed, the same curriculum is eight taps' worth of choice
   * on one screen. Expanded-by-default would reproduce exactly the screen being
   * complained about, so the default has to be the other way round even though it
   * hides content — and each head is written to say enough that opening it is a
   * decision rather than a guess.
   *
   * The exception is a curriculum with a single subject, where collapsing the only
   * section leaves a panel headed "the plan" with one line under it, which reads as
   * a plan that failed to build rather than as one that is folded up.
   */
  const [openByDefault, setOpenByDefault] = useState(sections.length === 1);
  /* His own choice per subject, which always beats the bulk default above. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const isExpanded = (nodeId: string) => expanded[nodeId] ?? openByDefault;

  /*
   * What he has just ticked or unticked, over what the API last told us.
   *
   * The checkbox moves from here rather than from the response, because a checkbox
   * that waits for a round trip before moving reads as broken — on a phone on
   * mobile data that is a visible half-second of nothing happening.
   */
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  /*
   * The last value the server actually confirmed, per node, which is where a failed
   * request has to put the checkbox back to.
   *
   * Not the same as the value in `nodes`: those props are the curriculum as it was
   * fetched and are not re-read after a toggle, so after one successful change they
   * are stale. Reverting to them would show him "active" for a subject he had
   * already switched off and the server still has off — a checkbox lying about
   * storage, which is the failure this whole optimistic path has to avoid.
   */
  const settled = useRef(new Map<string, boolean>());
  /* Which subject failed to save, and what to say about it. One at a time: the
     message belongs beside the box that snapped back, not in a list of complaints. */
  const [failure, setFailure] = useState<{ nodeId: string; message: string } | undefined>();

  const byId = useMemo(() => new Map(nodes.map((node) => [node.nodeId, node])), [nodes]);
  /*
   * "Absent means active" is not restated here — `isActiveNode` in the shared domain
   * is the one definition of it, and the API and the question-writing pass read it
   * through the same function. A second copy of that rule in the browser is how the
   * screen and the pipeline end up disagreeing about which subjects are being taught.
   */
  const isActive = (node: CurriculumNode) => chosen[node.nodeId] ?? isActiveNode(node, byId);

  /*
   * One request per node at a time, with the newest tap queued behind it.
   *
   * This is what stops a slow request and a fast second tap leaving the screen and
   * storage disagreeing. Firing both at once looks fine from here — the box shows
   * the last tap — but the two POSTs race at the API, and if the *first* one lands
   * second then storage ends up holding the value he tapped away from, with nothing
   * on screen to say so. So a second tap while one is in flight only records what he
   * now wants; the loop below picks it up when the current request settles and sends
   * it after, in order. The last tap is therefore always the last write.
   */
  const inFlight = useRef(new Set<string>());
  const queued = useRef(new Map<string, boolean>());

  async function choose(node: CurriculumNode, active: boolean) {
    const { nodeId } = node;
    setChosen((current) => ({ ...current, [nodeId]: active }));
    setFailure(undefined);

    if (inFlight.current.has(nodeId)) {
      queued.current.set(nodeId, active);
      return;
    }

    /*
     * Back to whatever the server last confirmed, which is not necessarily what the
     * props say — see `settled`.
     *
     * A checkbox that silently returns to where it was is indistinguishable from a
     * tap that missed, so this never happens without the message beside it: he would
     * otherwise spend a month believing a subject was switched off while nabu kept
     * writing questions for it.
     */
    const putBack = () => {
      const stored = settled.current.get(nodeId) ?? isActiveNode(node, byId);
      setChosen((current) => ({ ...current, [nodeId]: stored }));
    };

    inFlight.current.add(nodeId);
    let wanted = active;

    try {
      for (;;) {
        const result = await setNodeActive(objectiveId, nodeId, wanted);
        const next = queued.current.get(nodeId);
        queued.current.delete(nodeId);

        if (isError(result)) {
          putBack();
          setFailure({
            nodeId,
            message:
              result.error === 'sign_in_required' ? t('obj.error.signIn') : t('cur.active.failed'),
          });
          break;
        }

        settled.current.set(nodeId, result.active);
        // The server's own answer rather than the value that was sent, and a queued
        // tap asking for what it already confirmed needs no second request — three
        // quick taps land back where they started more often than not.
        if (next === undefined || next === result.active) {
          setChosen((current) => ({ ...current, [nodeId]: result.active }));
          break;
        }
        wanted = next;
      }
    } catch {
      /*
       * A request that never left the phone — a tunnel, aeroplane mode — arrives here
       * as a thrown error rather than as an `ApiError`, because `post` does not catch
       * the fetch itself. Reported as any other refusal is.
       *
       * The `finally` is the load-bearing half. Without it an exception would leave
       * this node in `inFlight` for the rest of the session, and every later tap on
       * that subject would queue behind a request that is never coming back — a
       * checkbox that moves and never saves again, silently.
       */
      putBack();
      setFailure({ nodeId, message: t('cur.active.failed') });
    } finally {
      queued.current.delete(nodeId);
      inFlight.current.delete(nodeId);
    }
  }

  /* Only sections with something inside them collapse, so only they answer to the
     bulk control — and with one such section there is nothing bulk about it. */
  const collapsible = sections.filter((section) => section.children.length > 0);
  const allExpanded =
    collapsible.length > 0 && collapsible.every((section) => isExpanded(section.head.node.nodeId));

  /*
   * Nothing left to teach, said plainly and not prevented.
   *
   * He may well be one tap from choosing the single subject he wants, and a screen
   * that refused the intermediate state would make that impossible. Saying it is
   * enough — the thing that must not happen is a curriculum that quietly teaches
   * nothing.
   */
  const nothingActive = sections.length > 0 && !sections.some((s) => isActive(s.head.node));

  return (
    <section className="panel">
      <h2>{t('cur.title')}</h2>
      <p className="muted small">
        {sections.length} {t('cur.subjects')} · {rows.length} {t('cur.topics')}
      </p>

      {/*
       * What this plan does not cover, at the top of it, in the learner's own terms (F-129).
       *
       * **A thin plan is a product and this notice is written as one.** A learner who asked
       * about something obscure gets the little there is; saying so plainly, with the missing
       * subjects named, is a better thing to hand them than either an error or a silence that
       * lets them believe the plan is the whole subject. It is never `.problem` — that tint is
       * the one thing in this palette that means something is wrong, and nothing here is.
       *
       * The two remedies are named in the sentence rather than offered as buttons, and that is
       * deliberate. Both of them are somebody else's panel — the uploader is `Sources`, the
       * second look is per subject in `Research` — and duplicating either here would be a second
       * copy of a control that has already been fixed twice. One button to where both of them
       * are is honest; two copies that drift are the failure `panels.ts` exists to prevent.
       *
       * **The reason used to be that they were on another route, and F-141 retired that.** Both
       * are now sections of this same screen, which is why the offer below is a jump rather than
       * a link — and why it is still one control and not three.
       */}
      {notice === 'unattributed' && <p className="coverage-note">{t('cov.unattributed')}</p>}

      {notice === 'thin' && (
        <div className="coverage-note">
          <h3 className="label">{t('cov.thin.title')}</h3>
          <p>
            {t('cov.thin.body')
              .replace('{inPlan}', String(coverage?.coverage.covered ?? 0))
              .replace('{researched}', String(coverage?.coverage.subjects.length ?? 0))}
          </p>
        </div>
      )}

      {missing.length > 0 && (
        <>
          {/* A short plan gets the subjects spelled out; a plan that is merely missing one gets
              a line and a link. The difference is what the learner is likely to be asking: at
              five of twelve the question is "what happened to the rest", and at eleven of
              twelve it is nothing at all until they notice. */}
          {notice === 'thin' ? (
            <>
              <h3 className="label">{t('cov.missing.title')}</h3>
              <p className="muted small">{t('cov.missing.body')}</p>
              <ul className="gaps">
                {shownMissing.map((subject) => (
                  <li key={subject.subjectId}>
                    {/*
                      The title, and no outcome pill beside it since F-158.

                      It said *little found* or *nothing found* — `cov.outcome.*`, the same three
                      words the research list printed — and they are a judgement about whether
                      enough was found for a subject whose threshold for enough only the learner
                      knows. Leo, 2026-09-19: *"its highly subject dependent (5000 characters may
                      be a LOT for some obscure things) and we cant make a judgement."* The
                      sentence below already says why this subject is not in the plan, which is
                      the fact the row exists to deliver.
                    */}
                    <p className="meta">
                      <strong>{subject.title}</strong>
                    </p>
                    {/* The words of the pass that decided, never paraphrased, and a sentence of
                        ours when it left none. A subject absent from a plan with no stated
                        reason is the bug this whole report exists to remove. */}
                    <p className="muted small">
                      {subject.why || (subject.whyKey ? t(subject.whyKey) : '')}
                    </p>
                  </li>
                ))}
              </ul>
              {(missing.length > shownMissing.length || allMissing) && (
                <button type="button" className="linky" onClick={() => setAllMissing(!allMissing)}>
                  {allMissing
                    ? t('cov.less')
                    : `${t('cov.more')} (${missing.length - shownMissing.length})`}
                </button>
              )}
            </>
          ) : (
            <p className="muted small">
              {t('cov.summary.missing').replace('{missing}', String(missing.length))}
            </p>
          )}
          <div className="actions">
            {/*
             * One tap from "my plan has five subjects" to "here is what happened to the other
             * seven", which is the question this feature was built to answer. Every subject says
             * its own outcome and carries its own two remedies in the research section, which
             * since F-141 is further down this same screen rather than a route away.
             */}
            <button type="button" className="button" onClick={goToResearch}>
              {t('cov.thin.action')}
            </button>
          </div>
        </>
      )}
      {/* The legend is written once here rather than as a tooltip on every badge.
          There is nothing to hover on a phone, and a `title` attribute is exactly
          the kind of explanation Leo would never see. */}
      <p className="muted small">{t('cur.weight.label')}</p>
      {/* What unticking costs, once, at the top of the list of checkboxes — which is
          where the decision is actually taken. Repeated on every row it would be
          sixty-nine copies of a sentence nobody reads twice, and it would push the
          subject titles apart until the outline was unscannable again. */}
      <p className="muted small">{t('cur.active.hint')}</p>

      {nothingActive && (
        <p className="problem" role="status">
          {t('cur.active.none')}
        </p>
      )}

      <div className="actions">
        <button type="button" className="linky" onClick={() => setFullSummaries(!fullSummaries)}>
          {fullSummaries ? t('cur.summaries.hide') : t('cur.summaries.show')}
        </button>
        {collapsible.length > 1 && (
          <button
            type="button"
            className="linky"
            onClick={() => {
              // The per-subject choices are dropped rather than rewritten, so this
              // button is the answer for every section including the ones he has
              // already opened by hand — otherwise "close every subject" would leave
              // those standing open and look broken.
              setExpanded({});
              setOpenByDefault(!allExpanded);
            }}
          >
            {allExpanded ? t('cur.sections.closeAll') : t('cur.sections.openAll')}
          </button>
        )}
      </div>

      <ol className={fullSummaries ? 'outline is-full' : 'outline'}>
        {sections.map(({ head, children }) => {
          const node = head.node;
          const active = isActive(node);
          const title = localised(node.title, locale, t('cur.untitled'));
          const summary = localised(node.summary, locale, '');
          /* Absent for a node that names no subject, and for one naming a subject the report
             does not carry — a subject deleted between the plan being built and this being read
             is a label we cannot write, not a reason to invent one. */
          const subject = node.subjectId ? subjectTitles.get(node.subjectId) : undefined;

          const heading = (
            <span className="section-main">
              <span className="node-head">
                <span className="node-number">{head.label}</span>
                <span className="node-title">{title}</span>
                {!active && <span className="pill pill-off">{t('cur.active.off')}</span>}
              </span>
              <span className="node-weight">
                <span className="weight-band">{t(bandOf(node.weight))}</span>
                <span className="weight-bar" aria-hidden="true">
                  <span style={{ width: barWidth(node.weight) }} />
                </span>
                {/* The count is the reason to open a section or leave it alone, so it
                    sits on the head itself. "Eleven topics inside" is what tells him
                    whether this is the subject that is eating his plan. */}
                {children.length > 0 && (
                  <span className="section-count">
                    {children.length}{' '}
                    {t(children.length === 1 ? 'cur.inside.one' : 'cur.inside.many')}
                  </span>
                )}
              </span>
              {/*
               * Which researched subject this branch was built from — the join, used (F-129).
               *
               * Nothing at all when the plan predates it, rather than "unknown subject" on every
               * section: two objectives are being taught from today whose nodes carry no subject
               * at all, and a label reading "unknown" down the length of their plans would say
               * something is wrong with a plan that is fine.
               */}
              {subject && <span className="node-subject">{t('cov.from').replace('{subject}', subject)}</span>}
              {summary && <span className="node-summary">{summary}</span>}
            </span>
          );

          return (
            <li key={node.nodeId} className={active ? 'section' : 'section is-off'}>
              <div className="section-head">
                {/*
                 * A checkbox only on a genuinely top-level node. A section head with a
                 * `parentId` is an orphan or a node in a `parentId` loop — rendered up
                 * here so it cannot vanish, but the API answers `not_top_level` for it
                 * by design, so offering a checkbox would be offering a tap that can
                 * only fail.
                 */}
                {!node.parentId && (
                  <label className="section-check">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(event) => void choose(node, event.currentTarget.checked)}
                      // Named with the subject, because "checkbox, checked" fifteen
                      // times over tells a screen reader user nothing about which
                      // subject they are switching off.
                      aria-label={`${t('cur.active.aria')}: ${title}`}
                    />
                  </label>
                )}

                {/* A subject with nothing under it is not a disclosure. A chevron that
                    opens onto an empty box is a bug report waiting to happen. */}
                {children.length > 0 ? (
                  <details
                    className="section-body"
                    open={isExpanded(node.nodeId)}
                    onToggle={(event) => {
                      const nowOpen = event.currentTarget.open;
                      setExpanded((current) => ({ ...current, [node.nodeId]: nowOpen }));
                    }}
                  >
                    <summary>{heading}</summary>
                    <ol className="outline">
                      {children.map((row) => (
                        <TopicRow
                          key={row.node.nodeId}
                          row={row}
                          // Only when it differs from the section's own subject: a subtree
                          // spliced in from elsewhere is worth pointing out, and eleven copies
                          // of the heading's own label are not.
                          {...(row.node.subjectId && row.node.subjectId !== node.subjectId
                            ? { subject: subjectTitles.get(row.node.subjectId) }
                            : {})}
                        />
                      ))}
                    </ol>
                  </details>
                ) : (
                  <div className="section-body section-flat">{heading}</div>
                )}
              </div>

              {failure?.nodeId === node.nodeId && (
                <p className="problem" role="alert">
                  {failure.message}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
